/**
 * Nutrition calculation engine for the account section
 * Handles nutrition recalculation and bundle suggestions
 */

import { useCallback, useMemo } from 'react';
import { calculateDailyIntakeFromForm } from '@/helpers/calorieCalculator';
import { caloriesFromGrams } from '@/domains/nutrition/FoodEnergy';
import { buildDogSubscriptionLineItems } from '@/domains/packs/PackOrchestrator';
import { SELLING_PLANS } from '@/domains/packs/SellingPlans';
import type {
  Dog,
  Recipe,
  NutritionOutput,
  BundleSuggestion,
  NutritionRecalculationResult,
  ShopifyProduct,
} from '../types';

interface UseNutritionEngineProps {
  products: ShopifyProduct[];
}

export function useNutritionEngine({ products }: UseNutritionEngineProps) {
  // Extract recipes from products
  const recipes = useMemo<Recipe[]>(() => {
    return products
      .filter((p) => p.product_type === 'Recipe' || p.tags?.includes('recipe'))
      .flatMap((p) => {
        return (p.variants || []).map((v) => ({
          id: String(v.id),
          name: v.title || p.title,
          protein: v.option1 || 'Unknown',
          variantId: v.id,
          caloriesPerGram: 1.26, // Default value
          image: p.images?.[0]?.src || p.image?.src,
        }));
      });
  }, [products]);

  /**
   * Calculate nutrition for a dog using the same calculator as the quiz
   * This ensures consistent results between quiz and account section
   */
  const calculateNutrition = useCallback(
    (dog: Dog): NutritionOutput => {
      const mealsPerDay = 2;

      // Use the same calculator as the quiz for consistent results
      const intake = calculateDailyIntakeFromForm({
        name: dog.name,
        breed: dog.breed,
        sex: dog.sex,
        reproduction: dog.reproduction,
        years: dog.ageYears ?? 1,
        months: dog.ageMonths ?? 0,
        weightKg: dog.weightKg,
        bodyCondition: dog.bodyCondition,
        activityLevel: dog.activityLevel,
        treatFrequency: dog.treatFrequency,
      }, { kcalPerGram: 1.65 });

      // Build meal distribution across recipes
      const totalMeals = mealsPerDay * 7; // meals per week
      const mealsPerRecipe: Record<string, number> = {};
      const gramsPerRecipe: Record<string, number> = {};

      if (recipes.length > 0) {
        const mealsEach = Math.floor(totalMeals / recipes.length);
        const remainder = totalMeals % recipes.length;
        const gramsPerMeal = intake.gramsPerDay / mealsPerDay;

        recipes.forEach((recipe, idx) => {
          const recipeMeals = mealsEach + (idx < remainder ? 1 : 0);
          mealsPerRecipe[recipe.id] = recipeMeals;
          gramsPerRecipe[recipe.id] = Math.round(recipeMeals * gramsPerMeal);
        });
      }

      return {
        caloriesPerDay: intake.dailyCalories,
        gramsPerDay: intake.gramsPerDay,
        mealsPerRecipe,
        gramsPerRecipe,
      };
    },
    [recipes]
  );

  /**
   * Compare previous and new nutrition
   */
  const compareNutrition = useCallback(
    (previous: NutritionOutput | null, current: NutritionOutput) => {
      if (!previous) {
        return null;
      }

      const gramsChange = current.gramsPerDay - previous.gramsPerDay;
      const caloriesChange = current.caloriesPerDay - previous.caloriesPerDay;

      const gramsChangePercent =
        previous.gramsPerDay !== 0
          ? (gramsChange / previous.gramsPerDay) * 100
          : 0;
      const caloriesChangePercent =
        previous.caloriesPerDay !== 0
          ? (caloriesChange / previous.caloriesPerDay) * 100
          : 0;

      const hasSignificantChange =
        Math.abs(gramsChangePercent) > 10 || Math.abs(caloriesChangePercent) > 10;

      return {
        gramsChange,
        gramsChangePercent,
        caloriesChange,
        caloriesChangePercent,
        hasSignificantChange,
      };
    },
    []
  );

  /**
   * Calculate total price from line items
   */
  const calculateTotalPrice = useCallback(
    (lineItems: any[]): number => {
      return lineItems.reduce((total, item) => {
        const variant = products
          .flatMap((p) => p.variants)
          .find((v) => String(v.id) === String(item.variantId));
        const price = parseFloat(variant?.price || '0');
        return total + price * item.quantity;
      }, 0);
    },
    [products]
  );

  /**
   * Determine recommended plan based on nutrition needs
   */
  const getRecommendedPlan = useCallback(
    (nutrition: NutritionOutput): "DAY_7" | "DAY_14" | "DAY_28" => {
      const gramsPerDay = nutrition.gramsPerDay;

      // Higher volume = recommend longer subscription for better value
      if (gramsPerDay > 500) return "DAY_28";
      if (gramsPerDay > 300) return "DAY_14";
      return "DAY_7";
    },
    []
  );

  /**
   * Generate bundle suggestions for all plan types
   */
  const suggestBundles = useCallback(
    (dog: Dog, nutrition: NutritionOutput, currentPlanKey?: string): BundleSuggestion[] => {
      const planKeys: Array<"DAY_7" | "DAY_14" | "DAY_28"> = ["DAY_7", "DAY_14", "DAY_28"];
      const recommendedPlan = getRecommendedPlan(nutrition);

      return planKeys.map((planKey) => {
        const planDays = parseInt(planKey.split('_')[1]);
        const mealsPerDay = 2;

        // Build subscription line items using domain logic
        const result = buildDogSubscriptionLineItems({
          dogForm: {
            ...dog,
            years: dog.ageYears ?? 1,
            months: dog.ageMonths ?? 0,
          } as any,
          recipes: recipes as any,
          planKey,
          totalMeals: mealsPerDay * planDays,
        });

        const totalPrice = calculateTotalPrice(result.lineItems);
        const pricePerDay = totalPrice / planDays;

        return {
          planKey,
          planLabel: `${planDays}-Day Plan`,
          planDays,
          lineItems: result.lineItems,
          packPlan: result.packPlan,
          nutrition: result.nutrition,
          totalPrice,
          pricePerDay,
          isRecommended: planKey === recommendedPlan,
          isCurrent: planKey === currentPlanKey,
          savings:
            planKey === "DAY_28"
              ? totalPrice * 0.1 // 10% savings on 28-day
              : planKey === "DAY_14"
              ? totalPrice * 0.05 // 5% savings on 14-day
              : undefined,
        };
      });
    },
    [recipes, getRecommendedPlan, calculateTotalPrice]
  );

  /**
   * Complete recalculation flow: calculate nutrition + suggest bundles + compare
   */
  const recalculateDogNutrition = useCallback(
    (dog: Dog): NutritionRecalculationResult => {
      // Get previous nutrition from dog data
      const previousNutrition: NutritionOutput | null = dog.currentNutrition
        ? {
            caloriesPerDay: caloriesFromGrams(dog.currentNutrition.dailyGrams),
            gramsPerDay: dog.currentNutrition.dailyGrams,
            mealsPerRecipe: dog.currentNutrition.mealsPerRecipe || {},
            gramsPerRecipe: dog.currentNutrition.gramsPerRecipe || {},
          }
        : dog.dailyCalories && dog.dailyGrams
        ? {
            caloriesPerDay: caloriesFromGrams(dog.dailyGrams),
            gramsPerDay: dog.dailyGrams,
            mealsPerRecipe: {},
            gramsPerRecipe: {},
          }
        : null;

      // Calculate new nutrition
      const newNutrition = calculateNutrition(dog);

      // Compare
      const comparison = compareNutrition(previousNutrition, newNutrition);

      // Generate bundle suggestions
      const bundleSuggestions = suggestBundles(
        dog,
        newNutrition,
        dog.subscription?.planKey
      );

      return {
        dog,
        previousNutrition,
        newNutrition,
        bundleSuggestions,
        comparison,
      };
    },
    [calculateNutrition, compareNutrition, suggestBundles]
  );

  return {
    recipes,
    calculateNutrition,
    compareNutrition,
    suggestBundles,
    recalculateDogNutrition,
  };
}
